/* eslint-disable react/no-unused-prop-types */
import { FC, useMemo, useState } from 'react';
import { Empty, FormInstance, Upload } from 'antd';
import UploadDragger from 'components/FileUpload/UploadDragger';
import { convertUploadToViewerFile } from 'components/FileUpload/useFileUpload';
import FileViewerModal, { FileViewerFile } from 'components/FileViewerModal';
import ButtonDownloadAllFiles, { ButtonDownloadAllFilesProps } from 'components/ButtonDownloadAllFiles';
import IconUploadFile from 'components/IconUploadFile';
import { removeImage } from 'utils/string.utils';
import useUploadDownload from 'utils/useUploadDownload';
import { FormBuilderFieldUpload } from 'types/formBuilder';
import { UploadFileApi } from 'types/upload-type';

interface FieldUploadProps {
  id?: string;
  value?: UploadFileApi[];
  data: FormBuilderFieldUpload;
  form: FormInstance;
  disabled: boolean;
  showDownloadIcon?: boolean;
  resource?: ButtonDownloadAllFilesProps['resource'];
  resourceIds?: ButtonDownloadAllFilesProps['ids'];
  onChange?: (values: UploadFileApi[]) => void;
  onRequest?: (response: { url: string; uuid: string }) => Promise<unknown>;
  onRemove?: (upload: UploadFileApi, textEditorUpdated: boolean) => void;
  onWrapUpdate?: (showCancelButton?: boolean, changedValues?: Record<string, any>, source?: 'main' | 'answer') => void;
}

const FieldUpload: FC<FieldUploadProps> = ({
  id,
  value = [],
  data,
  form,
  disabled,
  showDownloadIcon,
  resource,
  resourceIds,
  onChange,
  onRequest,
  onRemove,
  onWrapUpdate,
}) => {
  const { onDownload } = useUploadDownload();
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [fileViewerIndex, setFileViewerIndex] = useState(0);

  const viewerFiles = useMemo(
    () => (value ?? []).map(convertUploadToViewerFile).filter((f): f is FileViewerFile => f !== null),
    [value],
  );

  if (disabled) {
    if (!value?.length) {
      return <Empty description="No files" />;
    }

    return (
      <>
        {resource && resourceIds && <ButtonDownloadAllFiles resource={resource} ids={resourceIds} />}

        <Upload
          listType="picture"
          style={{ display: 'grid' }}
          showUploadList={{
            showDownloadIcon: true,
            showRemoveIcon: false,
          }}
          fileList={value}
          iconRender={(upload: UploadFileApi) => <IconUploadFile file={upload} />}
          onDownload={onDownload}
          onPreview={(file) => {
            if (file.status !== 'done' || !file.response?.uuid) return;
            const idx = viewerFiles.findIndex((f) => f.uuid === file.response!.uuid);
            if (idx === -1) return;
            setFileViewerIndex(idx);
            setFileViewerOpen(true);
          }}
        />

        <FileViewerModal
          files={viewerFiles}
          initialIndex={fileViewerIndex}
          open={fileViewerOpen}
          onClose={() => setFileViewerOpen(false)}
        />
      </>
    );
  }

  return (
    <UploadDragger
      type="picture"
      containerId={id}
      uploads={value}
      showDownloadIcon={showDownloadIcon}
      showRemoveIcon
      setUploads={(newUploads) => {
        onChange?.(newUploads);
        if (data?.name) {
          onWrapUpdate?.(false, { [data.name]: newUploads }, 'answer');
        }
      }}
      onRequest={onRequest}
      onRemove={(upload) => {
        const answers = form.getFieldsValue();

        let textEditorUpdated = false;

        Object.keys(answers).forEach((key: any) => {
          const item = answers[key];

          if (typeof item === 'string' && upload.response && item.includes(upload.response.url)) {
            textEditorUpdated = true;
            form.setFieldsValue({
              [key]: removeImage(item, upload.response.url),
            });
          }
        });

        onRemove?.(upload, textEditorUpdated);

        if (data?.name) {
          const newUploads = value.filter((f) => f.uid !== upload.uid);
          onWrapUpdate?.(false, { [data.name]: newUploads }, 'answer');
        }
      }}
    />
  );
};

export default FieldUpload;
